import { Alert, Box, Button, Card, CardContent, Container, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';

type LocationState = {
  message?: string;
};

function AuthBadge({ kind }: { kind: 'success' | 'warning' | 'login' }) {
  const palette =
    kind === 'success'
      ? { bg: 'success.main', icon: 'OK' }
      : kind === 'login'
        ? { bg: 'primary.main', icon: 'IN' }
        : { bg: 'warning.main', icon: '!' };

  return (
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: palette.bg,
      }}
    >
      <Typography variant="subtitle1" fontWeight={900} color="#fffefa">
        {palette.icon}
      </Typography>
    </Box>
  );
}

export default function AuthStatus() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? null;

  const flow = searchParams.get('flow');
  const status = searchParams.get('state');

  const config = (() => {
    if (flow === 'activation' && status === 'success') {
      return {
        badge: 'success' as const,
        title: '邮箱已激活',
        description: 'ProjectArgus 账户已经激活成功。现在可以返回登录页并进入控制台。',
        primaryLabel: '前往登录',
        primaryTo: '/login',
        secondaryLabel: '返回首页',
        secondaryTo: '/',
      };
    }
    if (flow === 'login' && status === 'failed') {
      return {
        badge: 'login' as const,
        title: '登录未成功',
        description:
          '系统未接受这次登录请求。最常见原因是账号尚未激活、用户名或密码有误，或激活邮件还未处理完成。',
        primaryLabel: '重新登录',
        primaryTo: '/login',
        secondaryLabel: '重新注册',
        secondaryTo: '/register',
      };
    }
    return {
      badge: 'warning' as const,
      title: '激活链接无效',
      description: '这个激活链接可能已经失效、被使用过，或复制时不完整。请重新注册或联系管理员。',
      primaryLabel: '重新注册',
      primaryTo: '/register',
      secondaryLabel: '返回登录',
      secondaryTo: '/login',
    };
  })();

  return (
    <Container
      maxWidth="sm"
      sx={{ flex: 1, display: 'flex', alignItems: 'center', py: { xs: 4, sm: 8 } }}
    >
      <Card sx={{ width: '100%' }}>
        <CardContent sx={{ p: { xs: 3, sm: 4.5 }, '&:last-child': { pb: { xs: 3, sm: 4.5 } } }}>
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <AuthBadge kind={config.badge} />
            <Stack spacing={1}>
              <Typography variant="h4" color="primary.dark">
                {config.title}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {config.description}
              </Typography>
            </Stack>

            {state?.message ? (
              <Alert severity="info" sx={{ width: '100%', textAlign: 'left' }}>
                {state.message}
              </Alert>
            ) : null}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: '100%' }}>
              <Button
                component={RouterLink}
                to={config.primaryTo}
                variant="contained"
                fullWidth
                size="large"
              >
                {config.primaryLabel}
              </Button>
              <Button
                component={RouterLink}
                to={config.secondaryTo}
                variant="outlined"
                fullWidth
                size="large"
              >
                {config.secondaryLabel}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
}
